import {
  CONTACT_EXPEDIENTE_DOC_TYPES,
  EXPEDIENTE_ID_TYPES,
  EXPEDIENTE_PAYMENT_METHODS,
  EXPEDIENTE_PERSON_KINDS,
  MARITAL_PROPERTY_REGIMES,
  MARITAL_STATUSES,
  type ContactExpediente,
  type ContactExpedienteDocType,
  type ContactExpedienteDocument,
  type ContactExpedienteInput,
  type ExpedientePersonKind,
} from "../../types";
import { validateClientRfc } from "../../clients/clientValidation";

/**
 * Almikia Epic EC2b (TASK-124): the pure rules of the contact expediente (sensitive PLD / contract
 * data). Single source for the value lists, the Spanish labels, the field validators and
 * `computeExpedienteMissing`, which MIRRORS `private.expediente_missing()` of TASK-123 (same
 * labels, same order, same rules). The messages are the SAME Spanish texts the SQL
 * `save_contact_expediente` / `register_contact_expediente_document` raise, so the Supabase and
 * FakeRest providers surface identical errors. No function here ever echoes an entered value.
 */

export {
  CONTACT_EXPEDIENTE_DOC_TYPES,
  EXPEDIENTE_ID_TYPES,
  EXPEDIENTE_PAYMENT_METHODS,
  EXPEDIENTE_PERSON_KINDS,
};

/** Private bucket behind `contact_expediente_documents` (never a public link). */
export const CONTACT_EXPEDIENTES_BUCKET = "contact-expedientes";

/** A signed download link lives one minute (same as the operation documents). */
export const CONTACT_EXPEDIENTE_SIGNED_URL_EXPIRY_SECONDS = 60;

/** Bucket limit (10 MB) and the file types `register_contact_expediente_document` accepts. */
export const EXPEDIENTE_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const EXPEDIENTE_ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
] as const;

export const EXPEDIENTE_ACCESS_DENIED_MESSAGE =
  "No tienes acceso al expediente de este contacto";
export const EXPEDIENTE_CONTACT_NOT_FOUND_MESSAGE = "Contacto no encontrado";
export const EXPEDIENTE_INVALID_DATA_MESSAGE =
  "Los datos del expediente no son válidos";

// ---------------------------------------------------------------------------------------------
// Spanish labels
// ---------------------------------------------------------------------------------------------

export const PERSON_KIND_LABELS: Record<ExpedientePersonKind, string> = {
  persona_fisica: "Persona física",
  persona_moral: "Persona moral",
};

export const MARITAL_STATUS_LABELS: Record<
  (typeof MARITAL_STATUSES)[number],
  string
> = {
  soltero: "Soltero(a)",
  casado: "Casado(a)",
  union_libre: "Unión libre",
  divorciado: "Divorciado(a)",
  viudo: "Viudo(a)",
};

export const MARITAL_PROPERTY_REGIME_LABELS: Record<
  (typeof MARITAL_PROPERTY_REGIMES)[number],
  string
> = {
  sociedad_conyugal: "Sociedad conyugal",
  separacion_bienes: "Separación de bienes",
};

export const ID_TYPE_LABELS: Record<
  (typeof EXPEDIENTE_ID_TYPES)[number],
  string
> = {
  ine: "INE",
  pasaporte: "Pasaporte",
  licencia: "Licencia de conducir",
  cedula_profesional: "Cédula profesional",
  otro: "Otro",
};

export const PAYMENT_METHOD_LABELS: Record<
  (typeof EXPEDIENTE_PAYMENT_METHODS)[number],
  string
> = {
  transferencia: "Transferencia",
  cheque: "Cheque",
  efectivo: "Efectivo",
  credito_hipotecario: "Crédito hipotecario",
  otro: "Otro",
};

export const DOC_TYPE_LABELS: Record<ContactExpedienteDocType, string> = {
  identificacion: "Identificación",
  comprobante_domicilio: "Comprobante de domicilio",
  acta_constitutiva: "Acta constitutiva",
  poder_representante: "Poder del representante",
  pld_soporte: "Soporte PLD",
};

/** Label of every writable expediente field, as the form shows it. */
export const EXPEDIENTE_FIELD_LABELS: Record<
  keyof ContactExpedienteInput,
  string
> = {
  person_kind: "Tipo de persona",
  rfc: "RFC",
  curp: "CURP",
  birth_date: "Fecha de nacimiento",
  birth_place: "Lugar de nacimiento",
  nationality: "Nacionalidad",
  marital_status: "Estado civil",
  marital_property_regime: "Régimen patrimonial",
  occupation: "Ocupación",
  address_street: "Calle",
  address_number: "Número",
  address_neighborhood: "Colonia",
  address_zip: "Código postal",
  address_municipality: "Municipio",
  address_state: "Estado",
  id_type: "Tipo de identificación",
  id_expires_on: "Vigencia de la identificación",
  legal_name: "Razón social",
  legal_representative_name: "Representante legal",
  funds_origin: "Origen de los recursos",
  payment_method: "Forma de pago",
  beneficial_owner_is_self: "El cliente es el beneficiario controlador",
  beneficial_owner_name: "Beneficiario controlador",
  is_pep: "Persona políticamente expuesta",
  pep_detail: "Detalle PEP",
};

/** The writable columns (the whitelist of `save_contact_expediente`). */
export const EXPEDIENTE_WRITABLE_FIELDS = Object.keys(
  EXPEDIENTE_FIELD_LABELS,
) as Array<keyof ContactExpedienteInput>;

const TEXT_FIELDS: ReadonlyArray<keyof ContactExpedienteInput> = [
  "person_kind",
  "rfc",
  "curp",
  "birth_place",
  "nationality",
  "marital_status",
  "marital_property_regime",
  "occupation",
  "address_street",
  "address_number",
  "address_neighborhood",
  "address_zip",
  "address_municipality",
  "address_state",
  "id_type",
  "legal_name",
  "legal_representative_name",
  "funds_origin",
  "payment_method",
  "beneficial_owner_name",
  "pep_detail",
];

// ---------------------------------------------------------------------------------------------
// Which documents each person kind needs
// ---------------------------------------------------------------------------------------------

/** Required documents per person kind (the PEP support document is conditional, see below). */
export const REQUIRED_DOC_TYPES_BY_PERSON_KIND: Record<
  ExpedientePersonKind,
  readonly ContactExpedienteDocType[]
> = {
  persona_fisica: ["identificacion", "comprobante_domicilio"],
  persona_moral: ["acta_constitutiva", "poder_representante"],
};

/** Required only when the contact declares being a PEP (`is_pep` true). */
export const PEP_DOC_TYPE: ContactExpedienteDocType = "pld_soporte";

// ---------------------------------------------------------------------------------------------
// Field validators (each returns a Spanish message, or undefined when valid)
// ---------------------------------------------------------------------------------------------

const isBlank = (value: unknown): boolean =>
  value == null || (typeof value === "string" && value.trim() === "");

const todayIso = (): string => new Date().toISOString().slice(0, 10);

/** True for a real calendar date written `YYYY-MM-DD`. */
export const isValidIsoDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

/** RFC: alphanumeric, 12 chars for persona_moral, 13 for persona_fisica (uppercased first). */
export const validateExpedienteRfc = (
  value: string | null | undefined,
  personKind: ExpedientePersonKind | null | undefined,
): string | undefined => {
  if (isBlank(value)) return undefined;
  const rfc = (value as string).trim().toUpperCase();
  if (!/^[A-Z0-9]+$/.test(rfc)) {
    return "El RFC solo puede contener letras y números";
  }
  const kind = personKind ?? "persona_fisica";
  if (validateClientRfc(rfc, { client_type: kind }) === undefined) {
    return undefined;
  }
  return kind === "persona_moral"
    ? "El RFC de una persona moral debe tener 12 caracteres"
    : "El RFC de una persona física debe tener 13 caracteres";
};

/** CURP: 18 alphanumeric for persona_fisica; never applicable to a persona_moral. */
export const validateExpedienteCurp = (
  value: string | null | undefined,
  personKind: ExpedientePersonKind | null | undefined,
): string | undefined => {
  if (isBlank(value)) return undefined;
  if (personKind === "persona_moral") {
    return "La CURP no aplica a una persona moral";
  }
  return /^[A-Z0-9]{18}$/.test((value as string).trim().toUpperCase())
    ? undefined
    : "La CURP debe tener 18 caracteres alfanuméricos";
};

/** Postal code: exactly 5 digits. */
export const validateExpedienteZip = (
  value: string | null | undefined,
): string | undefined => {
  if (isBlank(value)) return undefined;
  return /^[0-9]{5}$/.test((value as string).trim())
    ? undefined
    : "El código postal debe tener 5 dígitos";
};

/** Birth date: a real date, never in the future. */
export const validateExpedienteBirthDate = (
  value: string | null | undefined,
): string | undefined => {
  if (isBlank(value)) return undefined;
  if (!isValidIsoDate(value as string)) {
    return "La fecha de nacimiento no es una fecha válida";
  }
  return (value as string) > todayIso()
    ? "La fecha de nacimiento no puede ser futura"
    : undefined;
};

/** ID expiry: a real date (an expired one is allowed to be saved; it counts as missing). */
export const validateExpedienteIdExpiry = (
  value: string | null | undefined,
): string | undefined =>
  isBlank(value) || isValidIsoDate(value as string)
    ? undefined
    : "La vigencia de la identificación no es una fecha válida";

/**
 * Validates and normalises a document about to be registered (same texts as
 * `register_contact_expediente_document`, plus the 10 MB bucket limit checked before uploading).
 */
export const validateExpedienteDocumentFile = (file: {
  name: string;
  type: string;
  size: number;
}): string | undefined => {
  if (isBlank(file.name)) return "El nombre del archivo es obligatorio";
  if (
    !(EXPEDIENTE_ALLOWED_MIME_TYPES as readonly string[]).includes(file.type)
  ) {
    return "El tipo de archivo no está permitido (PDF, PNG, JPEG o WebP)";
  }
  if (!file.size || file.size <= 0) return "El archivo está vacío";
  if (file.size > EXPEDIENTE_MAX_FILE_BYTES) {
    return "El archivo supera el máximo de 10 MB";
  }
  return undefined;
};

export const isExpedienteDocType = (
  value: unknown,
): value is ContactExpedienteDocType =>
  (CONTACT_EXPEDIENTE_DOC_TYPES as readonly unknown[]).includes(value);

// ---------------------------------------------------------------------------------------------
// Save: whitelist + normalisation + validation (mirrors `save_contact_expediente`)
// ---------------------------------------------------------------------------------------------

/** Date format check only (the future check runs once the merged row is known). */
const validateExpedienteBirthDateFormat = (
  value: string,
): string | undefined =>
  isValidIsoDate(value)
    ? undefined
    : "La fecha de nacimiento no es una fecha válida";

/** Throws an Error carrying a Spanish message when the merged expediente is not valid. */
const fail = (message: string): never => {
  throw new Error(message);
};

/**
 * Applies a PARTIAL save over the current row, exactly like `save_contact_expediente`: unknown
 * keys are ignored, blank strings become null, only the keys present in `input` change, and the
 * same Spanish validations run in the same order. Returns the normalised writable fields (RFC /
 * CURP uppercased, CURP nulled for persona_moral, regime nulled unless casado, beneficial owner
 * name nulled when the contact is her own). Throws an Error with the Spanish message otherwise.
 */
export const resolveExpedienteSave = (
  current: Partial<ContactExpediente> | null | undefined,
  input: unknown,
): ContactExpedienteInput => {
  if (input == null || typeof input !== "object" || Array.isArray(input)) {
    return fail(EXPEDIENTE_INVALID_DATA_MESSAGE);
  }
  const raw = input as Record<string, unknown>;
  const incoming: Record<string, unknown> = {};
  for (const field of EXPEDIENTE_WRITABLE_FIELDS) {
    if (!(field in raw)) continue;
    const value = raw[field];
    incoming[field] =
      typeof value === "string" && value.trim() === "" ? null : value;
  }

  for (const field of ["beneficial_owner_is_self", "is_pep"] as const) {
    if (
      field in incoming &&
      incoming[field] !== null &&
      typeof incoming[field] !== "boolean"
    ) {
      return fail(EXPEDIENTE_INVALID_DATA_MESSAGE);
    }
  }
  for (const field of TEXT_FIELDS) {
    if (
      field in incoming &&
      incoming[field] !== null &&
      typeof incoming[field] !== "string"
    ) {
      return fail(EXPEDIENTE_INVALID_DATA_MESSAGE);
    }
  }
  for (const field of ["birth_date", "id_expires_on"] as const) {
    if (!(field in incoming) || incoming[field] === null) continue;
    const message =
      typeof incoming[field] === "string"
        ? (field === "birth_date"
            ? validateExpedienteBirthDateFormat
            : validateExpedienteIdExpiry)(incoming[field] as string)
        : fail(EXPEDIENTE_INVALID_DATA_MESSAGE);
    if (message) return fail(message);
  }

  const merged: Record<string, unknown> = {};
  for (const field of EXPEDIENTE_WRITABLE_FIELDS) {
    merged[field] =
      field in incoming
        ? incoming[field]
        : ((current as Record<string, unknown> | null | undefined)?.[field] ??
          null);
  }
  const next = merged as ContactExpedienteInput;

  if (
    !next.person_kind ||
    !(EXPEDIENTE_PERSON_KINDS as readonly string[]).includes(next.person_kind)
  ) {
    return fail("Selecciona el tipo de persona (física o moral)");
  }
  const enums: Array<[unknown, readonly string[], string]> = [
    [next.marital_status, MARITAL_STATUSES, "El estado civil no es válido"],
    [
      next.marital_property_regime,
      MARITAL_PROPERTY_REGIMES,
      "El régimen patrimonial no es válido",
    ],
    [
      next.id_type,
      EXPEDIENTE_ID_TYPES,
      "El tipo de identificación no es válido",
    ],
    [
      next.payment_method,
      EXPEDIENTE_PAYMENT_METHODS,
      "La forma de pago no es válida",
    ],
  ];
  for (const [value, allowed, message] of enums) {
    if (value != null && !allowed.includes(value as string)) fail(message);
  }

  const rfcMessage = validateExpedienteRfc(next.rfc, next.person_kind);
  if (rfcMessage) return fail(rfcMessage);
  if (next.rfc) next.rfc = next.rfc.trim().toUpperCase();

  if (next.person_kind === "persona_moral") {
    if (incoming.curp != null)
      return fail("La CURP no aplica a una persona moral");
    next.curp = null;
  } else {
    const curpMessage = validateExpedienteCurp(next.curp, next.person_kind);
    if (curpMessage) return fail(curpMessage);
    if (next.curp) next.curp = next.curp.trim().toUpperCase();
  }

  const zipMessage = validateExpedienteZip(next.address_zip);
  if (zipMessage) return fail(zipMessage);
  if (next.address_zip) next.address_zip = next.address_zip.trim();

  if (next.birth_date && next.birth_date > todayIso()) {
    return fail("La fecha de nacimiento no puede ser futura");
  }

  if (next.marital_status === "casado") {
    if (!next.marital_property_regime) {
      return fail("Indica el régimen patrimonial del matrimonio");
    }
  } else {
    next.marital_property_regime = null;
  }

  if (
    next.beneficial_owner_is_self === false &&
    isBlank(next.beneficial_owner_name)
  ) {
    return fail("Indica el nombre del beneficiario controlador");
  }
  if (next.beneficial_owner_is_self === true) next.beneficial_owner_name = null;

  return next;
};

/** Names (never values) of the writable fields whose value differs between two rows. */
export const changedExpedienteFieldNames = (
  before: Partial<ContactExpediente> | null | undefined,
  after: Partial<ContactExpediente>,
): string[] =>
  EXPEDIENTE_WRITABLE_FIELDS.filter((field) => {
    const a = (before as Record<string, unknown> | null | undefined)?.[field];
    const b = (after as Record<string, unknown>)[field];
    return (a ?? null) !== (b ?? null);
  }).sort();

// ---------------------------------------------------------------------------------------------
// Missing list (mirrors `private.expediente_missing`)
// ---------------------------------------------------------------------------------------------

/**
 * Spanish labels of what is missing from a contact's expediente (never values); an empty array
 * means complete. MIRRORS `private.expediente_missing()` of TASK-123: same labels, same order, same
 * rules. No expediente at all returns 'Tipo de persona' plus the whole persona_fisica + common
 * list; casado needs the regime; an expired ID counts as missing; deleted documents are ignored;
 * the PEP support document is required only when `is_pep` is true.
 */
export const computeExpedienteMissing = (
  expediente: Partial<ContactExpediente> | null | undefined,
  documents: ReadonlyArray<
    Pick<ContactExpedienteDocument, "doc_type" | "deleted_at">
  >,
  today: string = todayIso(),
): string[] => {
  const v = expediente ?? {};
  const docs = new Set(
    documents.filter((d) => !d.deleted_at).map((d) => d.doc_type),
  );
  const missing: string[] = [];
  const add = (condition: boolean, label: string) => {
    if (condition) missing.push(label);
  };

  add(!expediente, "Tipo de persona");

  if (v.person_kind === "persona_moral") {
    add(isBlank(v.legal_name), "Razón social");
    add(isBlank(v.rfc), "RFC");
    add(isBlank(v.legal_representative_name), "Representante legal");
    add(!docs.has("acta_constitutiva"), "Documento: acta constitutiva");
    add(!docs.has("poder_representante"), "Documento: poder del representante");
  } else {
    add(isBlank(v.rfc), "RFC");
    add(isBlank(v.curp), "CURP");
    add(!v.birth_date, "Fecha de nacimiento");
    add(isBlank(v.birth_place), "Lugar de nacimiento");
    add(isBlank(v.nationality), "Nacionalidad");
    if (isBlank(v.marital_status)) {
      missing.push("Estado civil");
    } else if (
      v.marital_status === "casado" &&
      isBlank(v.marital_property_regime)
    ) {
      missing.push("Régimen patrimonial");
    }
    add(isBlank(v.occupation), "Ocupación");
    add(isBlank(v.address_street), "Calle");
    add(isBlank(v.address_number), "Número");
    add(isBlank(v.address_neighborhood), "Colonia");
    add(isBlank(v.address_zip), "Código postal");
    add(isBlank(v.address_municipality), "Municipio");
    add(isBlank(v.address_state), "Estado");
    add(isBlank(v.id_type), "Tipo de identificación");
    add(
      !v.id_expires_on || v.id_expires_on < today,
      "Vigencia de la identificación",
    );
    add(!docs.has("identificacion"), "Documento: identificación");
    add(
      !docs.has("comprobante_domicilio"),
      "Documento: comprobante de domicilio",
    );
  }

  add(isBlank(v.funds_origin), "Origen de los recursos");
  add(isBlank(v.payment_method), "Forma de pago");
  add(
    !(v.beneficial_owner_is_self === true || !isBlank(v.beneficial_owner_name)),
    "Beneficiario controlador",
  );
  if (v.is_pep == null) {
    missing.push("Declaración PEP");
  } else if (v.is_pep && !docs.has(PEP_DOC_TYPE)) {
    missing.push("Documento: soporte PLD");
  }
  return missing;
};
